import React from 'react';
import { Card, Space, DatePicker, Input, message, Select } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { useGetMatWarehouses, useGetMatGapWhInventory } from '../../../hooks/genericHooks';
import { formatNumberSmart } from '../../../utils/numberFormat';
import { SearchOutlined } from '@ant-design/icons';
import MatGapMaterialSelect from '../../../components/modals/MatGapMaterialSelect';
import Button from '../../../components/common/Button';

// Reusable core transaction list shell
// Props:
// - title: Card title
// - opTypes: array of operation types to include (e.g., ['incoming','transfer','issue','assign','return','sendback','adjustment'])
// - defaultFilters: optional default filter values

export type MatGapOpType =
  | 'incoming'
  | 'transfer'
  | 'issue'
  | 'assign'
  | 'return'
  | 'sendback'
  | 'adjustment';

export interface CoreTxnListFilters {
  dateRange?: [string, string] | null;
  reference?: string;
  matId?: string | null;
  matText?: string | null; // filters description and notes
  whFrom?: number | null;
  whTo?: number | null;
  assignee?: string | null; // person/company code or id (string for flexibility)
  issuedTo?: string | null;
  createdBy?: string | null;
}

interface MatGapCoreTransactionListProps {
  title: string;
  opTypes: MatGapOpType[];
  defaultFilters?: CoreTxnListFilters;
}

const { RangePicker } = DatePicker;

export default function MatGapCoreTransactionList({ title, opTypes, defaultFilters }: MatGapCoreTransactionListProps) {
  const [filters, setFilters] = React.useState<CoreTxnListFilters>(() => ({
    dateRange: defaultFilters?.dateRange ?? null,
    reference: defaultFilters?.reference ?? '',
    matId: (defaultFilters?.matId as any as string) ?? null,
    matText: null,
    whFrom: defaultFilters?.whFrom ?? null,
    whTo: defaultFilters?.whTo ?? null,
    assignee: defaultFilters?.assignee ?? null,
    issuedTo: null,
    createdBy: defaultFilters?.createdBy ?? null,
  }));
  const [loading, setLoading] = React.useState(false);
  const [allRows, setAllRows] = React.useState<any[]>([]);
  const { data: whDataRaw = [] } = useGetMatWarehouses() as any;
  const warehouses: any[] = Array.isArray(whDataRaw) ? whDataRaw : (whDataRaw?.data ?? []);
  const whMap = React.useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach((w: any) => m.set(Number(w.id), String(w.name)));
    return m;
  }, [warehouses]);

  // Material description map from inventory (covers described materials in current system)
  const { data: invRaw = [] } = useGetMatGapWhInventory() as any;
  const invList: any[] = Array.isArray(invRaw) ? invRaw : (invRaw?.data ?? []);
  const matDescMap = React.useMemo(() => {
    const m = new Map<string, string>();
    (invList || []).forEach((row: any) => {
      const id = String(row.mat_id);
      if (!m.has(id)) m.set(id, String(row.description || ''));
    });
    return m;
  }, [invList]);

  // Material select modal state
  const [matSelectOpen, setMatSelectOpen] = React.useState(false);

  const escape = (v: string) => v.replace(/'/g, "''");

  const buildWhere = React.useCallback(() => {
    const conds: string[] = [];
    if (opTypes?.length) {
      const inList = opTypes.map(t => `'${escape(String(t))}'`).join(',');
      conds.push(`op_type IN (${inList})`);
    }
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      conds.push(`txn_date >= '${escape(filters.dateRange[0])}' AND txn_date <= '${escape(filters.dateRange[1])}'`);
    }
    return conds.length ? `WHERE ${conds.join(' AND ')}` : '';
  }, [opTypes, filters.dateRange]);

  const runQuery = React.useCallback(async () => {
    const where = buildWhere();
    const sql = `
      SELECT id, op_type, txn_date, reference_no, from_warehouse_id, to_warehouse_id,
             mat_id, qty, unit, assignee_id, issued_to, created_by, notes
      FROM mat_gap_txns
      ${where}
      ORDER BY txn_date DESC, id DESC
    `;
    try {
      setLoading(true);
      const data = await fetchSqlDataWithQuery(sql);
      const arr = Array.isArray(data) ? data : (data?.rows || data?.data || []);
      setAllRows(arr || []);
    } catch (e) {
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [buildWhere]);

  React.useEffect(() => {
    runQuery();
  }, [runQuery]);

  const handleDateChange: RangePickerProps['onChange'] = (vals) => {
    if (!vals || !Array.isArray(vals)) {
      setFilters((p) => ({ ...p, dateRange: null }));
      return;
    }
    setFilters((p) => ({
      ...p,
      dateRange: [vals[0]?.format('YYYY-MM-DD') || '', vals[1]?.format('YYYY-MM-DD') || ''] as [string, string],
    }));
  };

  // TODO: replace with fetchSqlData(useEffect) to load data using opTypes + filters
  // Keeping it as a shell for now so pages can adopt this component immediately.

  // Client-side filtering for cascading selects
  const filteredRows = React.useMemo(() => {
    return (allRows || []).filter((r) => {
      if (filters.reference && r.reference_no !== filters.reference) return false;
      if (filters.matId) {
        const needle = String(filters.matId).trim().toLowerCase();
        const idHay = String(r.mat_id ?? '').toLowerCase();
        if (!idHay.includes(needle)) return false;
      }
      if (filters.matText) {
        const needle = String(filters.matText).trim().toLowerCase();
        const descHay = String(matDescMap.get(String(r.mat_id)) || '').toLowerCase();
        const notesHay = String(r.notes || '').toLowerCase();
        if (!(descHay.includes(needle) || notesHay.includes(needle))) return false;
      }
      if (filters.whFrom && Number(r.from_warehouse_id) !== Number(filters.whFrom)) return false;
      if (filters.whTo && Number(r.to_warehouse_id) !== Number(filters.whTo)) return false;
      if (filters.assignee) {
        const s = String(filters.assignee).toLowerCase();
        const a = (r.assignee_id ? String(r.assignee_id) : '').toLowerCase();
        const i = (r.issued_to ? String(r.issued_to) : '').toLowerCase();
        if (a !== s && i !== s) return false;
      }
      if (filters.issuedTo && String(r.issued_to || '').toLowerCase() !== String(filters.issuedTo).toLowerCase()) return false;
      if (filters.createdBy && String(r.created_by || '').toLowerCase() !== String(filters.createdBy).toLowerCase()) return false;
      return true;
    });
  }, [allRows, filters, matDescMap]);

  // Helpers to generate cascaded options (apply all filters except the current field)
  const distinct = (vals: any[]) => Array.from(new Set(vals.filter(v => v !== undefined && v !== null && v !== '')));

  const optionsFor = (field: 'reference' | 'whFrom' | 'whTo' | 'assignee' | 'issuedTo' | 'createdBy') => {
    const base = (allRows || []).filter((r) => {
      // apply other filters but skip the current field
      if (field !== 'reference' && filters.reference && r.reference_no !== filters.reference) return false;
      if (field !== 'whFrom' && filters.whFrom && Number(r.from_warehouse_id) !== Number(filters.whFrom)) return false;
      if (field !== 'whTo' && filters.whTo && Number(r.to_warehouse_id) !== Number(filters.whTo)) return false;
      if (field !== 'assignee' && filters.assignee) {
        const s = String(filters.assignee).toLowerCase();
        const a = (r.assignee_id ? String(r.assignee_id) : '').toLowerCase();
        const i = (r.issued_to ? String(r.issued_to) : '').toLowerCase();
        if (a !== s && i !== s) return false;
      }
      if (field !== 'issuedTo' && filters.issuedTo && String(r.issued_to || '').toLowerCase() !== String(filters.issuedTo).toLowerCase()) return false;
      if (field !== 'createdBy' && filters.createdBy && String(r.created_by || '').toLowerCase() !== String(filters.createdBy).toLowerCase()) return false;
      if (filters.matId) {
        const needle = String(filters.matId).trim().toLowerCase();
        const idHay = String(r.mat_id ?? '').toLowerCase();
        const descHay = String(matDescMap.get(String(r.mat_id)) || '').toLowerCase();
        if (!(idHay.includes(needle) || descHay.includes(needle))) return false;
      }
      return true;
    });

    switch (field) {
      case 'reference':
        return distinct(base.map(r => r.reference_no)).map(v => ({ value: v, label: v }));
      case 'whFrom':
        return distinct(base.map(r => r.from_warehouse_id)).map((v: number) => ({ value: v, label: whMap.get(Number(v)) || String(v) }));
      case 'whTo':
        return distinct(base.map(r => r.to_warehouse_id)).map((v: number) => ({ value: v, label: whMap.get(Number(v)) || String(v) }));
      case 'assignee': {
        const ids = distinct(base.map(r => r.assignee_id));
        const names = distinct(base.map(r => r.issued_to));
        const merged = distinct([...ids, ...names].map(String));
        return merged.map(v => ({ value: v, label: v }));
      }
      case 'issuedTo':
        return distinct(base.map(r => r.issued_to)).map((v: string) => ({ value: v, label: v }));
      case 'createdBy':
        return distinct(base.map(r => r.created_by)).map((v: string) => ({ value: v, label: v }));
    }
  };

  const columns = React.useMemo(() => [
    { title: 'Type', dataIndex: 'op_type', key: 'op_type', width: 120 },
    { title: 'Date', dataIndex: 'txn_date', key: 'txn_date', width: 110, render: (v: any) => (v ? dayjs(v).format('YYYY-MM-DD') : '') },
    { title: 'Reference', dataIndex: 'reference_no', key: 'reference_no', width: 160 },
    { title: 'From WH', dataIndex: 'from_warehouse_id', key: 'from_warehouse_id', width: 160, render: (v: number) => whMap.get(Number(v)) || v },
    { title: 'To WH', dataIndex: 'to_warehouse_id', key: 'to_warehouse_id', width: 160, render: (v: number) => whMap.get(Number(v)) || v },
    { title: 'Mat ID', dataIndex: 'mat_id', key: 'mat_id', width: 110, align: 'right' },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 110, align: 'right', render: (v: unknown) => formatNumberSmart(v) },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90 },
    { title: 'Assignee ID', dataIndex: 'assignee_id', key: 'assignee_id', width: 160, align: 'left' },
    { title: 'Issued To', dataIndex: 'issued_to', key: 'issued_to', width: 160 },
    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', width: 140 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 220 },
  ], [whMap]);

  return (
    <Card title={title} extra={<span>Ops: {opTypes.join(', ')}</span>}>
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={
            filters.dateRange
              ? [dayjs(filters.dateRange[0] || undefined), dayjs(filters.dateRange[1] || undefined)]
              : null
          }
          onChange={handleDateChange}
        />
        <Select
          placeholder="Reference"
          allowClear
          showSearch
          value={filters.reference || undefined}
          onChange={(v) => setFilters((p) => ({ ...p, reference: (v || '') as string }))}
          options={optionsFor('reference')}
          style={{ width: 200 }}
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Space.Compact>
          <Input
            placeholder="Material ID"
            value={filters.matId ?? ''}
            onChange={(e) => setFilters((p) => ({ ...p, matId: (e.target.value || '').trim() || null }))}
            style={{ width: 180 }}
            allowClear
          />
          <Button icon={<SearchOutlined />} onClick={() => setMatSelectOpen(true)} aria-label="Select material" />
        </Space.Compact>
        <Input
          placeholder="Material Description / Notes"
          value={filters.matText ?? ''}
          onChange={(e) => setFilters((p) => ({ ...p, matText: (e.target.value ?? '') }))}
          style={{ width: 240 }}
          allowClear
        />
        <Select
          placeholder="From WH"
          allowClear
          value={filters.whFrom ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, whFrom: (v as number) ?? null }))}
          options={optionsFor('whFrom')}
          style={{ width: 180 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="To WH"
          allowClear
          value={filters.whTo ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, whTo: (v as number) ?? null }))}
          options={optionsFor('whTo')}
          style={{ width: 180 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="Assignee"
          allowClear
          value={filters.assignee ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, assignee: (v as string) ?? null }))}
          options={optionsFor('assignee')}
          style={{ width: 220 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="Created By"
          allowClear
          value={filters.createdBy ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, createdBy: (v as string) ?? null }))}
          options={optionsFor('createdBy')}
          style={{ width: 200 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="Issued To"
          allowClear
          value={filters.issuedTo ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, issuedTo: (v as string) ?? null }))}
          options={optionsFor('issuedTo')}
          style={{ width: 200 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Button type="primary" onClick={runQuery}>Search</Button>
        <Button onClick={() => setFilters({ dateRange: null, reference: '', matId: null, matText: null, whFrom: null, whTo: null, assignee: null, issuedTo: null, createdBy: null })}>Reset</Button>
      </Space>

      <StandardTable
        columns={columns as any}
        dataSource={filteredRows as any}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
      />

      {/* Material select modal */}
      <MatGapMaterialSelect
        open={matSelectOpen}
        onCancel={() => setMatSelectOpen(false)}
        onOk={(selected) => {
          // pick the first selected material id
          const first = Array.isArray(selected) && selected.length > 0 ? selected[0] : undefined;
          if (first && first.mat_id != null) {
            setFilters((p) => ({ ...p, matId: String(first.mat_id) }));
          }
          setMatSelectOpen(false);
        }}
        title="Select Material"
      />
    </Card>
  );
}
